import React from "react";
import { useDispatch } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { postJwtLogin } from "../../services/auth";
import { loginUser } from "../../Features/Auth/authSlice";
import { syncCartOnLogin } from "../../Features/Cart/cartActions";
import { currentUser } from "../../Features/Auth/thunk";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const URLmessage = searchParams.get("message") || "";
  const [message, setMessage] = React.useState(URLmessage);
  const [isShowPassword, setIsShowPassword] = React.useState(false);

  const from = location.state?.from || "/"; // fallback

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: postJwtLogin,
  });

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      identifier: "" || "",
      password: "" || "",
    },
    validationSchema: Yup.object({
      identifier: Yup.string().required("Please Enter Your Username"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      let reqData = { user: { ...values } };
      setMessage("");
      mutate(reqData, {
        onSuccess: async (data) => {
          localStorage.setItem("authUser", JSON.stringify(data));
          localStorage.setItem("refreshToken", data?.refreshToken);
          // dispatch(loginUser(data));

          // sync local store to API then fetch
          await dispatch(currentUser());
          await dispatch(syncCartOnLogin());

          navigate(from, { replace: true });
        },
        onError: (error) => {
          console.log(error);
        },
      });
    },
  });

  return (
    <section className="account py-80">
      <div className="container container-lg">
        <form
          action="#"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="row gy-4">
            {/* Login Card Start */}
            <div className="col-lg-3 col-xl-3 mx-auto">
              <div className="border border-gray-100 hover-border-main-600 transition-1 rounded-16 px-24 py-40 h-100">
                {/* <h6 className="text-xl mb-32">Login</h6> */}
                <div className="mb-32">
                  <h6 className="text-xl text-dark mb-1">Welcome Back !</h6>
                  <p className="text-muted">
                    Sign in to continue to phAMACart.
                  </p>
                </div>

                {message && (
                  <div className="alert alert-success border-0" role="alert">
                    {message}
                  </div>
                )}
                {isError && (
                  <div className="alert alert-danger border-0" role="alert">
                    {error}
                  </div>
                )}

                <div className="mb-16">
                  <label
                    htmlFor="username"
                    className="text-neutral-900 text-sm mb-8 fw-medium"
                  >
                    Username or email address{" "}
                    <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      validation.touched.identifier &&
                      validation.errors.identifier &&
                      "is-invalid"
                    }`}
                    id="identifier"
                    name="identifier"
                    placeholder="Enter username"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.identifier || ""}
                  />
                </div>
                <div className="mb-16">
                  <label
                    htmlFor="password"
                    className="text-neutral-900 text-sm mb-8 fw-medium"
                  >
                    Password
                  </label>
                  <div className="position-relative auth-pass-inputgroup mb-3">
                    <input
                      type={isShowPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={validation.values.password || ""}
                      className={`form-control pe-5 ${
                        validation.touched.password &&
                        validation.errors.password &&
                        "is-invalid"
                      }`}
                      placeholder="Enter Password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                    <span
                      className={`toggle-password position-absolute top-50 inset-inline-end-0 me-16 translate-middle-y cursor-pointer`}
                      id="#enter-password"
                      onClick={() => setIsShowPassword(!isShowPassword)}
                    >
                      <i
                        className={`${isShowPassword ? "ph ph-eye-slash" : "ph ph-eye"}`}
                      />
                    </span>
                  </div>
                  {/* <div className="position-relative">
                    <input
                      type="password"
                      className="common-input"
                      id="password"
                      placeholder="Enter Password"
                      defaultValue="password"
                    />
                    <span
                      className="toggle-password position-absolute top-50 inset-inline-end-0 me-16 translate-middle-y cursor-pointer ph ph-eye-slash"
                      id="#password"
                    />
                  </div> */}
                </div>
                <div className="mb-16">
                  <div className="flex-align gap-48 flex-wrap">
                    {/* <button type="submit" className="btn btn-main py-18 px-40">
                      Log in
                    </button> */}
                    <button
                      type="submit"
                      className="btn btn-main w-100"
                      disabled={isPending}
                    >
                      {isPending && (
                        <div
                          className="spinner-border spinner-border-sm me-5"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                      Sign In
                    </button>
                    <div className="form-check common-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        defaultValue=""
                        id="remember"
                        disabled
                      />
                      <label
                        className="form-check-label flex-grow-1"
                        htmlFor="remember"
                      >
                        Remember me
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-24 text-center">
                  <p className="mb-0">
                    Don't have an account ?{" "}
                    <Link
                      className="fw-semibold text-primary text-decoration-underline"
                      to="/auth-register"
                      data-discover="true"
                    >
                      Signup
                    </Link>
                  </p>
                </div>
                {/* <div className="mt-24">
                  <Link
                    to="#"
                    className="text-danger-600 text-sm fw-semibold hover-text-decoration-underline"
                  >
                    Forgot your password?
                  </Link>
                </div> */}
              </div>
            </div>
            {/* Login Card End */}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Login;
